/**
 * Electric aurora colours shared by the LINE assets: the menu-background
 * ribbons (app-line-menu-bg) and the tab ON glyphs (app-line-menu-button) both
 * derive their neon green/blue/pink from the site colour circles 2..4 with the
 * SAME math, mix towards a neon anchor, then an HSL saturation boost, so
 * every LINE asset matches 1:1. The Python generator (line-themes/
 * generate-line-theme.py) mirrors these formulas; keep them in sync.
 */
import { Product } from '../../model/product/product.model';

export interface ElectricTrio {
  green: string;
  greenCore: string;
  blue: string;
  blueCore: string;
  pink: string;
  pinkCore: string;
}

/** The theme's electric aurora trio (+ brighter cores for ribbon bodies). */
export function electricTrio( p: Product ): ElectricTrio {
  const t: any = p.designTokens || {};
  const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
  const blue = cp[ 1 ] || t.primaryColor || '#549EFF';
  const teal = cp[ 2 ] || t.secondaryColor || '#1ECFB6';
  const pink = cp[ 3 ] || t.tertiaryColor || '#BB719B';
  const greenBase = mixHex( teal, '#2BFF6E', 0.45 );
  const pinkBase = mixHex( pink, '#FF3DA6', 0.45 );
  return {
    green: vividHex( greenBase, 0.35, 0.10 ),
    greenCore: vividHex( greenBase, 0.40, 0.20 ),
    blue: vividHex( blue, 0.50, 0.02 ),
    blueCore: vividHex( blue, 0.50, 0.10 ),
    pink: vividHex( pinkBase, 0.45, 0.06 ),
    pinkCore: vividHex( pinkBase, 0.50, 0.16 ),
  };
}

/** Blend two hex colours: f=0 -> a, f=1 -> b. */
export function mixHex( a: string, b: string, f: number ): string {
  const ca = rgb( a );
  const cb = rgb( b );
  return hex( ca[ 0 ] + ( cb[ 0 ] - ca[ 0 ] ) * f, ca[ 1 ] + ( cb[ 1 ] - ca[ 1 ] ) * f, ca[ 2 ] + ( cb[ 2 ] - ca[ 2 ] ) * f );
}

/** Boost saturation + lightness in HSL space, plain lighten() moves toward
 *  white and KILLS saturation; this keeps the aurora colours luminous. */
export function vividHex( hexCol: string, satAdd: number, lightAdd: number ): string {
  const [ r, g, b ] = rgb( hexCol ).map( v => v / 255 );
  const max = Math.max( r, g, b );
  const min = Math.min( r, g, b );
  let h = 0;
  const l = ( max + min ) / 2;
  const d = max - min;
  let s = 0;
  if ( d !== 0 ) {
    s = d / ( 1 - Math.abs( 2 * l - 1 ) );
    if ( max === r ) { h = ( ( g - b ) / d ) % 6; }
    else if ( max === g ) { h = ( b - r ) / d + 2; }
    else { h = ( r - g ) / d + 4; }
    h *= 60;
    if ( h < 0 ) { h += 360; }
  }
  const s2 = Math.min( 1, Math.max( 0, s + satAdd ) );
  const l2 = Math.min( 1, Math.max( 0, l + lightAdd ) );
  const c = ( 1 - Math.abs( 2 * l2 - 1 ) ) * s2;
  const x = c * ( 1 - Math.abs( ( h / 60 ) % 2 - 1 ) );
  const m = l2 - c / 2;
  let rr = 0, gg = 0, bb = 0;
  if ( h < 60 ) { rr = c; gg = x; }
  else if ( h < 120 ) { rr = x; gg = c; }
  else if ( h < 180 ) { gg = c; bb = x; }
  else if ( h < 240 ) { gg = x; bb = c; }
  else if ( h < 300 ) { rr = x; bb = c; }
  else { rr = c; bb = x; }
  return hex( ( rr + m ) * 255, ( gg + m ) * 255, ( bb + m ) * 255 );
}

function rgb( hexCol: string ): number[] {
  const h = ( hexCol || '#000000' ).replace( '#', '' );
  const full = h.length === 3 ? h.split( '' ).map( c => c + c ).join( '' ) : h;
  return [ 0, 2, 4 ].map( i => parseInt( full.substr( i, 2 ), 16 ) || 0 );
}

function hex( r: number, g: number, b: number ): string {
  return '#' + [ r, g, b ].map( v => Math.max( 0, Math.min( 255, Math.round( v ) ) ).toString( 16 ).padStart( 2, '0' ) ).join( '' );
}
